import React from "react";
import { FormControlLabel, Checkbox } from "@mui/material";

/**
 * A boolean field component that renders a checkbox with a label positioned above it.
 * The checkbox is left-aligned horizontally relative to the label.
 *
 * @component
 * @param {Object} props - The component props
 * @param {Object} props.formStateHandlers - Form state handlers from react-use-form-state
 * @param {Function} props.formStateHandlers.checkbox - Checkbox handler function
 * @param {Object} props.fieldSetting - Field configuration object
 * @param {Function} props.fieldSetting.onChange - Original change handler function
 * @param {string} [props.className] - Additional CSS class names
 * @param {string} props.label - The label text to display above the checkbox
 * @param {boolean} [props.required=false] - Whether the field is required
 * @param {boolean} [props.disabled=false] - Whether the checkbox should be disabled
 * @returns {JSX.Element} A Material-UI FormControlLabel containing a Checkbox
 *
 * @example
 * ```jsx
 * <BooleanField
 *   formStateHandlers={formStateHandlers}
 *   fieldSetting={fieldSetting}
 *   label="Accept Terms"
 *   required={true}
 *   disabled={false}
 * />
 * ```
 */
export default function BooleanField({
  formStateHandlers,
  fieldSetting,
  ...props
}) {
  const { checkbox } = formStateHandlers;
  const { className: externalClassName, label, required, disabled } = props;

  const classNames = externalClassName?.split(" ") ?? [];

  /**
   * Handles checkbox change events by modifying the event object
   * to include the checked value in the target.value property.
   *
   * @param {Event} e - The original change event
   * @returns {*} Result of calling the fieldSetting.onChange function
   */
  const onChange = (e) => {
    const moddedEvent = {
      ...e,
      target: {
        ...e.target,
        value: e.target.checked,
      },
    };
    return fieldSetting.onChange(moddedEvent);
  };

  const fieldConfig = checkbox({
    ...fieldSetting,
    onChange,
  });

  // Derived properties
  /** @type {string} Normalized string representation of the checked value */
  const normalizedChecked = String(fieldConfig?.checked ?? true).toLowerCase();

  /** @type {boolean} Whether the checkbox should be checked */
  const isChecked = normalizedChecked === "true" || normalizedChecked === "1";

  /** @type {boolean} Whether the checkbox should be disabled (disabled prop or required + checked) */
  const isDisabled = disabled || (isChecked && required);

  return (
    <FormControlLabel
      sx={{
        alignItems: "flex-start",
        marginX: "8px",
      }}
      labelPlacement='top'
      label={label}
      className={classNames.join(" ")}
      control={
        <Checkbox
          {...{
            ...fieldConfig,
            color: "primary",
            disabled: isDisabled,
            checked: isChecked,
            required,
          }}
        />
      }
    />
  );
}
